import type { ColDef, CellStyle } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useState, useRef, useMemo } from "react";
import { PivotModule, ColumnsToolPanelModule } from "ag-grid-enterprise";
import { Button, Space, Spin, Tag } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { DataCopyClipboard } from "../data-copy-clipboard";
import { DataType } from "@src/types/intelligence-search";
import { formatNumber } from "@src/util/intelligence-search/asset-renderer";

ModuleRegistry.registerModules([AllCommunityModule, PivotModule, ColumnsToolPanelModule]);

// Format numbers with compact notation (1K, 1M, etc.)
const formatCompactNumber = (num: number): string => {
    return formatNumber(num, { notation: "compact", compactDisplay: "short" });
};

export type FieldDataType = "string" | "number" | "boolean" | "date";

export interface FieldTypeConfig {
    [fieldName: string]: FieldDataType;
}

export interface FieldConfig {
    dimensions: string[];
    measures: string[];
}

export interface RendererMapping {
    [fieldName: string]: (value: any) => React.ReactNode;
}

export interface StyleConfig {
    headerStyle?: CellStyle | ((fieldName: string) => CellStyle);
    cellStyle?: CellStyle | ((fieldName: string) => CellStyle);
}

interface PivotTableProps<T> {
    data: T[];
    totalRows: number;
    fieldConfig: FieldConfig;
    fieldTypes: FieldTypeConfig;
    loading?: boolean;
    height?: string | number;
    width?: string | number;
    className?: string;
    rendererMapping?: RendererMapping;
    customStyle?: StyleConfig;
}

export const convertDataTypeToFieldDataType = (dataType: DataType): FieldDataType => {
    const mappingDataTypes: Record<DataType, FieldDataType> = {
        bool: "boolean",
        int: "number",
        float: "number",
        string: "string",
        date: "date",
    };
    return mappingDataTypes[dataType];
};
const getAggregationFunctions = (fieldType: FieldDataType): string[] => {
    if (fieldType === "number") {
        return ["sum", "avg", "min", "max", "count"];
    } else if (fieldType === "boolean") {
        return ["count", "first", "last"];
    } else if (fieldType === "date") {
        return ["min", "max", "count", "first", "last"];
    } else {
        return ["count", "first", "last"];
    }
};

const createCellRendererWithNullHandling = (
    customRenderer?: (value: any) => any,
    rendererFromMapping?: (value: any) => React.ReactNode,
) => {
    return (params: any) => {
        let value = params.value;

        if (value && typeof value === "object" && value.hasOwnProperty("value")) {
            value = value.value;
        }
        // Check if this is a null/undefined value
        if (value === null || value === undefined) {
            // Don't show null tag for aggregated cells that are empty due to grouping logic
            const isAggregatedRow = params.node?.group || params.node?.footer;
            const hasAggFunction = params.colDef?.aggFunc;

            // Show null tag only for raw data that is actually null, not for empty aggregated cells
            if (!isAggregatedRow && !hasAggFunction) {
                return <Tag color="default">null</Tag>;
            }

            // For aggregated cells that are empty, return empty string or dash
            return "";
        }

        if (rendererFromMapping) {
            return rendererFromMapping(value);
        }

        return customRenderer ? customRenderer(value) : value;
    };
};

const createColumnDef = <T,>(
    key: string,
    fieldConfig: FieldConfig,
    fieldTypes: FieldTypeConfig,
    rendererMapping?: RendererMapping,
    customStyle?: StyleConfig,
): ColDef<T> => {
    const fieldType = fieldTypes[key] || "string";

    const isNumber = fieldType === "number";
    const isBoolean = fieldType === "boolean";
    const isDate = fieldType === "date";

    const isDimension = fieldConfig.dimensions.includes(key);

    const rendererFromMapping = rendererMapping?.[key];

    // Get styles using function or direct value
    const getCellStyle = (): CellStyle | undefined => {
        if (!customStyle?.cellStyle) return undefined;
        return typeof customStyle.cellStyle === "function" ? customStyle.cellStyle(key) : customStyle.cellStyle;
    };

    const getHeaderStyle = (): CellStyle | undefined => {
        if (!customStyle?.headerStyle) return undefined;
        return typeof customStyle.headerStyle === "function" ? customStyle.headerStyle(key) : customStyle.headerStyle;
    };

    const colDef: ColDef<T> = {
        field: key as any,
        headerName: formatHeaderName(key),
        sortable: true,
        resizable: true,
        filter: true,
        valueGetter: (params) => {
            return (params.data as any)?.[key];
        },
        cellRenderer: createCellRendererWithNullHandling(undefined, rendererFromMapping),
        cellStyle: getCellStyle(),
        headerStyle: getHeaderStyle(),
    };

    // Configure pivot settings based on field config
    if (isDimension) {
        colDef.enablePivot = true;
        colDef.enableRowGroup = true;
        colDef.enableValue = false;
        colDef.allowedAggFuncs = ["count", "first", "last"];
    } else {
        colDef.enablePivot = false;
        colDef.enableRowGroup = false;
        colDef.enableValue = true;

        const aggFunctions = getAggregationFunctions(fieldType);
        colDef.allowedAggFuncs = aggFunctions;

        if (isNumber) {
            colDef.aggFunc = "sum";
        } else {
            colDef.aggFunc = "count";
        }
    }
    if (isNumber) {
        // Merge custom cellStyle with default number style
        const customCellStyle = getCellStyle();
        colDef.cellStyle = customCellStyle ? { ...customCellStyle, textAlign: "right" } : { textAlign: "right" };
        colDef.filter = "agNumberColumnFilter";
        // Only apply default number renderer if no custom renderer from mapping
        if (!rendererFromMapping) {
            colDef.cellRenderer = createCellRendererWithNullHandling((value) => {
                if (isNaN(value)) {
                    return "-";
                }
                return Number(value).toLocaleString();
            });
        }
    } else if (isBoolean) {
        colDef.filter = "agTextColumnFilter";
        // Only apply default boolean renderer if no custom renderer from mapping
        if (!rendererFromMapping) {
            colDef.cellRenderer = createCellRendererWithNullHandling((value) => {
                return value ? "✓" : "✗";
            });
        }
    } else if (isDate) {
        colDef.filter = "agDateColumnFilter";
        // Only apply default date renderer if no custom renderer from mapping
        if (!rendererFromMapping) {
            colDef.cellRenderer = createCellRendererWithNullHandling((value) => {
                if (!value) return "-";
                const date = new Date(value);
                return date.toLocaleDateString();
            });
        }
    } else {
        colDef.filter = "agTextColumnFilter";
    }
    return colDef;
};

const formatHeaderName = (fieldName: string): string => {
    return fieldName
        .replace(/[._]/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

export const PivotTable = <T extends Record<string, any>>({
    data,
    totalRows,
    fieldConfig,
    fieldTypes,
    loading = false,
    height = "500px",
    width = "100%",
    className = "",
    rendererMapping,
    customStyle,
}: PivotTableProps<T>): JSX.Element => {
    const gridRef = useRef<AgGridReact<T>>(null);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [displayedData, setDisplayedData] = useState<any[]>([]);

    const columnDefs = useMemo<ColDef<T>[]>(() => {
        if (!data || data.length === 0) return [];

        const firstRow = data[0];
        const keys = Object.keys(firstRow);

        return keys.map((key) => createColumnDef(key, fieldConfig, fieldTypes, rendererMapping, customStyle));
    }, [data, fieldConfig, fieldTypes, rendererMapping, customStyle]);

    const defaultColDef: ColDef<T> = {
        flex: 1,
        sortable: true,
        resizable: true,
        minWidth: 200,
        filter: true,
    };

    const toggleSidebar = () => {
        if (gridRef.current?.api) {
            if (sidebarVisible) {
                gridRef.current.api.closeToolPanel();
            } else {
                gridRef.current.api.openToolPanel("columns");
            }
            setSidebarVisible(!sidebarVisible);
        }
    };
    const updateDisplayedData = React.useCallback(() => {
        if (!gridRef.current?.api) return;

        const newDisplayedData: any[] = [];

        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
            if (node.data) {
                newDisplayedData.push(node.data);
            }
        });

        setDisplayedData(newDisplayedData);
    }, [gridRef]);

    const handleSortChanged = () => {
        updateDisplayedData();
    };

    const handleFilterChanged = () => {
        updateDisplayedData();
    };

    const handleColumnRowGroupChanged = () => {
        updateDisplayedData();
    };

    const dataLength = data.length;
    return (
        <div className={`relative ${className}`} style={{ width, height }}>
            <div className="absolute -top-10 right-2.5 z-10 flex gap-2 items-center  rounded-md  p-1">
                <DataCopyClipboard rawData={displayedData} />
                <Button type="default" icon={<SettingOutlined />} onClick={toggleSidebar} size="small">
                    {sidebarVisible ? "Hide Panel" : "Show Panel"}
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-full flex-col gap-4">
                    <Spin size="large" />
                    <div className="text-gray-500 text-sm">Loading data...</div>
                </div>
            ) : (
                <>
                    <div className="flex justify-end absolute -bottom-8 right-2">
                        {dataLength < totalRows ? (
                            <Space size={"small"}>
                                <span>Showing</span>
                                <span className="font-bold">{formatCompactNumber(dataLength)}</span>
                                <span>of</span>
                                <span className="font-bold">{formatCompactNumber(totalRows || 0)}</span>
                                <span>records</span>
                            </Space>
                        ) : (
                            <Space size={"small"}>
                                <span>Showing</span>
                                <span>all</span>
                                <span className="font-bold">{formatCompactNumber(dataLength)}</span>
                                <span>records</span>
                            </Space>
                        )}
                    </div>

                    <AgGridReact<T>
                        ref={gridRef}
                        rowData={data}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={100}
                        paginationPageSizeSelector={[50, 100, 200, 500]}
                        suppressAggFuncInHeader={false}
                        groupDefaultExpanded={1}
                        sideBar={{
                            toolPanels: [
                                {
                                    id: "columns",
                                    labelDefault: "Columns",
                                    labelKey: "columns",
                                    iconKey: "columns",
                                    toolPanel: "agColumnsToolPanel",
                                    minWidth: 250,
                                    maxWidth: 300,
                                    width: 250,
                                    toolPanelParams: {
                                        suppressPivotMode: true,
                                        suppressRowGroups: false,
                                        suppressValues: false,
                                        suppressColumnFilter: false,
                                        suppressColumnSelectAll: false,
                                        suppressColumnExpandAll: false,
                                    },
                                },
                                {
                                    id: "filters",
                                    labelDefault: "Filters",
                                    labelKey: "filters",
                                    iconKey: "filter",
                                    toolPanel: "agFiltersToolPanel",
                                },
                            ],

                            defaultToolPanel: "columns",
                            hiddenByDefault: !sidebarVisible,
                        }}
                        onGridReady={(params) => {
                            if (sidebarVisible) {
                                params.api.openToolPanel("columns");
                            }
                            updateDisplayedData();
                        }}
                        onToolPanelVisibleChanged={(params) => {
                            const isVisible = params.api.isToolPanelShowing();
                            setSidebarVisible(isVisible);
                        }}
                        onSortChanged={handleSortChanged}
                        onFilterChanged={() => {
                            handleFilterChanged();
                        }}
                        onRowDataUpdated={() => {
                            updateDisplayedData();
                        }}
                        onColumnRowGroupChanged={handleColumnRowGroupChanged}
                    />
                </>
            )}
        </div>
    );
};
